import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckList,
  ErrorNotice,
  Field,
  SelectInput,
  StripePayment,
  TextInput,
  type ChoiceOption,
  type StripePaymentOutcome,
  type StripePaymentQuote,
} from '@bcecloud/portal-ui';
import {
  confirmPayLinkPayment,
  paySeriesPayment,
  quoteSeriesPayment,
  type PayLinkSchedule,
  type SeriesChoice,
  type SeriesEventView,
  type SeriesPayLinkView,
} from '@/lib/pay-link';
import { CHOICE, Checkout, EMAIL, MIN_PAYMENT, PaidInFull, ProcessingNotice, Receipt, SummaryRow, longDate, money, parseAmount } from '@/components/PayCheckout';

/**
 * A series contract's payment page: one link for all its events. Every event
 * with something owed starts ticked at its next payment; the payer can untick
 * events, pay an event's full balance, or type another amount, and one charge
 * covers the lot. The server re-checks every event and amount.
 */

/** What can be paid on one event: each open line, its full balance, or another amount. */
function amountOptions(schedule: PayLinkSchedule): { key: string; label: string }[] {
  const outstanding = schedule.lines.filter((line) => line.outstanding > 0);
  const options = outstanding.map((line) => ({ key: line.key, label: `${line.label}: ${money(line.outstanding)}` }));
  if (outstanding.length > 1) options.push({ key: CHOICE.total, label: `Full balance: ${money(schedule.amountDue)}` });
  options.push({ key: CHOICE.custom, label: 'Another amount' });
  return options;
}

/** "Oct 14", to tell a series' events apart in the summary. */
function shortDate(iso: string | null): string | null {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : null;
}

/** The event's name for the payer: its program, else its contract number. */
function eventTitle(event: SeriesEventView): string {
  return event.programName ?? (event.contractNumber ? `Contract #${event.contractNumber}` : 'Event');
}

/** One event's choice as what the API charges, or why it can't be charged yet. */
function resolveEvent(event: SeriesEventView, key: string, customAmount: string): SeriesChoice | string {
  const { schedule } = event;
  if (key === CHOICE.total) return { eventId: event.eventId, line: CHOICE.total, amount: schedule.amountDue };
  if (key === CHOICE.custom) {
    const amount = parseAmount(customAmount);
    const title = eventTitle(event);
    if (amount === null || amount < MIN_PAYMENT) return `Enter an amount of at least ${money(MIN_PAYMENT)} for ${title}.`;
    if (amount > schedule.amountDue) return `That's more than the ${money(schedule.amountDue)} still owed on ${title}.`;
    return { eventId: event.eventId, line: CHOICE.custom, amount };
  }
  const line = schedule.lines.find((candidate) => candidate.key === key);
  return line ? { eventId: event.eventId, line: line.key, amount: line.outstanding } : `Choose what to pay on ${eventTitle(event)}.`;
}

/** The royal panel: each chosen event and the total, with a card fee once one is quoted. */
function SeriesSummary({
  view,
  rows,
  total,
  feeQuote = null,
}: {
  readonly view: SeriesPayLinkView;
  readonly rows: readonly { readonly key: string; readonly label: string; readonly amount: number }[];
  readonly total: number;
  readonly feeQuote?: StripePaymentQuote | null;
}): React.ReactElement {
  const count = view.events.length;
  return (
    <>
      <p className="text-portal-small text-white/80">Pay Best Corporate Events</p>
      <p className="mt-1 text-[40px] leading-tight font-bold tracking-[-1px]">{money(feeQuote?.total ?? total)}</p>
      {feeQuote ? (
        <p className="text-portal-small text-white/80">
          {money(feeQuote.amount)} + {money(feeQuote.fee)} credit card fee ({feeQuote.feePercent}%)
        </p>
      ) : null}
      <p className="mt-3 text-portal-small text-white/80">{[view.accountName, `${count} ${count === 1 ? 'event' : 'events'} on one contract`].filter(Boolean).join(' · ')}</p>
      {rows.length > 0 ? (
        <dl className="mt-8 divide-y divide-white/20 border-y border-white/20">
          {rows.map((row) => (
            <SummaryRow key={row.key} label={row.label} value={money(row.amount)} />
          ))}
          <SummaryRow label="Total" value={money(total)} strong />
        </dl>
      ) : null}
    </>
  );
}

/**
 * The series payment view.
 *
 * @param props - The link's token and what it opens.
 * @returns The page.
 */
export function SeriesPay({ token, view }: { readonly token: string; readonly view: SeriesPayLinkView }): React.ReactElement {
  const queryClient = useQueryClient();
  // Unticked rather than ticked: every open event starts in, and a refreshed
  // balance needs no syncing.
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const [lineKeys, setLineKeys] = useState<Readonly<Record<string, string>>>({});
  const [customAmounts, setCustomAmounts] = useState<Readonly<Record<string, string>>>({});
  const [email, setEmail] = useState('');
  const [feeQuote, setFeeQuote] = useState<StripePaymentQuote | null>(null);
  const [done, setDone] = useState<{ outcome: StripePaymentOutcome; amount: number; email: string } | null>(null);

  const open = view.events.filter((event) => !event.paidInFull);
  const ticked = open.filter((event) => !unticked.has(event.eventId));
  const resolved = ticked.map((event) => {
    const key = lineKeys[event.eventId] ?? amountOptions(event.schedule)[0]?.key ?? CHOICE.custom;
    return { event, key, choice: resolveEvent(event, key, customAmounts[event.eventId] ?? '') };
  });
  const problems = resolved.flatMap((entry) => (typeof entry.choice === 'string' ? [entry.choice] : []));
  const valid = resolved.flatMap((entry) => (typeof entry.choice === 'string' ? [] : [{ event: entry.event, choice: entry.choice }]));
  const problem = ticked.length === 0 ? 'Choose at least one event to pay.' : (problems[0] ?? null);
  const choices: SeriesChoice[] | null = problem ? null : valid.map((entry) => entry.choice);
  const total = Math.round((choices ?? []).reduce((sum, choice) => sum + choice.amount, 0) * 100) / 100;
  const summaryRows = problem
    ? []
    : valid.map(({ event, choice }) => ({
        key: event.eventId,
        label: [eventTitle(event), shortDate(event.eventDate)].filter(Boolean).join(', '),
        amount: choice.amount,
      }));

  if (done) {
    return (
      <Checkout summary={<SeriesSummary view={view} rows={[]} total={done.amount} />}>
        <Receipt outcome={done.outcome} amount={done.amount} email={done.email} />
      </Checkout>
    );
  }

  if (view.paidInFull) {
    return (
      <Checkout summary={<SeriesSummary view={view} rows={[]} total={0} />}>
        <PaidInFull message="Nothing is owed on these events. Thank you." />
      </Checkout>
    );
  }

  const options: ChoiceOption<string>[] = view.events.map((event) => {
    const detail = [longDate(event.eventDate), event.contractNumber ? `Contract #${event.contractNumber}` : null].filter(Boolean).join(' · ');
    const choice = resolved.find((entry) => entry.event.eventId === event.eventId)?.choice;
    const value = event.paidInFull ? 'Paid' : choice && typeof choice !== 'string' ? money(choice.amount) : undefined;
    return { key: event.eventId, label: eventTitle(event), ...(detail ? { detail } : {}), ...(value ? { value } : {}), disabled: event.paidInFull };
  });

  function toggle(eventId: string, checked: boolean): void {
    setUnticked((current) => {
      const next = new Set(current);
      if (checked) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }

  /** Everything on the page that must be right before Stripe charges. */
  function beforePay(): string | null {
    if (problem) return problem;
    return EMAIL.test(email.trim()) ? null : 'Enter the email address the receipt should go to.';
  }

  return (
    <Checkout summary={<SeriesSummary view={view} rows={summaryRows} total={total} feeQuote={feeQuote} />}>
      <ProcessingNotice since={view.processingSince} />

      <h2 className="mb-2 text-portal-small font-bold text-portal-ink">Events to pay</h2>
      <CheckList
        label="Events to pay"
        options={options}
        values={new Set(ticked.map((event) => event.eventId))}
        onToggle={toggle}
        renderChecked={(eventId) => {
          const event = view.events.find((candidate) => candidate.eventId === eventId);
          const entry = resolved.find((candidate) => candidate.event.eventId === eventId);
          if (!event || !entry) return null;
          const inputId = `pay-amount-${eventId}`;
          return (
            <div className="space-y-2">
              <SelectInput
                aria-label={`What to pay on ${eventTitle(event)}`}
                value={entry.key}
                onChange={(change) => setLineKeys((current) => ({ ...current, [eventId]: change.target.value }))}
              >
                {amountOptions(event.schedule).map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
              {entry.key === CHOICE.custom ? (
                <Field
                  label="Amount"
                  htmlFor={inputId}
                  hint={typeof entry.choice === 'string' && customAmounts[eventId] ? entry.choice : `Up to ${money(event.schedule.amountDue)}.`}
                >
                  <TextInput
                    id={inputId}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={customAmounts[eventId] ?? ''}
                    onChange={(change) => setCustomAmounts((current) => ({ ...current, [eventId]: change.target.value }))}
                  />
                </Field>
              ) : null}
            </div>
          );
        }}
      />

      <div className="mt-6">
        <Field label="Email for your receipt" htmlFor="pay-email" required>
          <TextInput id="pay-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
      </div>

      <h2 className="mt-6 mb-2 text-portal-small font-bold text-portal-ink">Payment method</h2>
      {view.publishableKey ? (
        <StripePayment
          publishableKey={view.publishableKey}
          amountCents={choices ? Math.round(total * 100) : null}
          amountLabel={choices ? money(total) : ''}
          cardSurchargePercent={view.cardSurchargePercent}
          formatMoney={money}
          onFeeQuote={setFeeQuote}
          beforePay={beforePay}
          quote={(confirmationTokenId) => {
            if (!choices) throw new Error(problem ?? 'Choose what to pay.');
            return quoteSeriesPayment(token, choices, confirmationTokenId);
          }}
          pay={(confirmationTokenId, expectedTotalCents) => {
            if (!choices) throw new Error(problem ?? 'Choose what to pay.');
            return paySeriesPayment(token, choices, confirmationTokenId, expectedTotalCents, email.trim());
          }}
          confirmPayment={(paymentIntentId) => confirmPayLinkPayment(token, paymentIntentId)}
          onComplete={(outcome) => {
            setDone({ outcome, amount: total, email: email.trim() });
            void queryClient.invalidateQueries({ queryKey: ['pay-link', token] });
          }}
        />
      ) : (
        <ErrorNotice title="Online payment is unavailable" message="Please contact Best Corporate Events to pay this balance." />
      )}
    </Checkout>
  );
}
